<template>
    <div class="record-materials-modal-material__container">
        <div class="record-materials-modal-material__content">
            <div class="record-materials-modal-material__name-header">
                {{ material.name }}
            </div>

            <div class="record-materials-modal-material__fields-grid">
                <div class="record-materials-modal-material__field-row">
                    <span class="record-materials-modal-material__field-label">
                        {{ $t("ProductionProgress.RequiredQuantity") }}
                    </span>
                    <span class="record-materials-modal-material__field-value">
                        {{ formatValue(material.quantityRequired) }}
                    </span>
                </div>

                <div class="record-materials-modal-material__field-row">
                    <span class="record-materials-modal-material__field-label">
                        {{ $t("ProductionProgress.AlreadyNoted") }}
                    </span>
                    <span class="record-materials-modal-material__field-value">
                        {{ formatValue(material.alreadyConsumed) }}
                    </span>
                </div>

                <div class="record-materials-modal-material__field-row --input-row">
                    <span class="record-materials-modal-material__field-label --highlight">
                        {{ $t("ProductionProgress.QuantityToNote") }}
                    </span>

                    <div
                        v-if="renderField"
                        class="record-materials-modal-material__quantity-box"
                    >
                        <b-form-group :invalid-feedback="$t('RequiredField')">
                            <b-form-input
                                type="text"
                                class="record-materials-modal-material__quantity-input"
                                :value="material.quantityConsumed"
                                :state="material.valid"
                                placeholder="0,00"
                                @input="updateConsumedQuantity"
                                :formatter="handleFormatterQuantityConsumed"
                                @blur="validateQuantityConsumed"
                            />
                        </b-form-group>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="record-materials-modal-material__close-button-container"
            @click="handleOpenRemoveMaterialModal(material)"
        >
            <close-icon />
        </div>
    </div>
</template>

<script>
    import { BFormGroup, BFormInput } from "bootstrap-vue";
    import { mapGetters, mapMutations } from "vuex";
    import CloseIcon from "@core/assets/formatted-icons/circle-wrong.svg";
    import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
    import { filterMaterials } from "@/views/pages/operator/components/RecordMaterialsModal/utils";
    import {
        formatCurrencyWithoutSymbol,
        handleConsumedRequired,
        handleFormatQuantityConsumedToNumber
    } from "@/views/pages/operator/utils";

    export default {
        components: { BFormGroup, BFormInput, CloseIcon, ToastificationContent },
        props: {
            material: {
                type: Object,
                required: true,
                default: () => ({
                    id: -1,
                    name: "",
                    quantityConsumed: 0
                })
            },
            orderId: {
                type: Number,
                default: null
            }
        },
        data() {
            return {
                renderField: true
            };
        },
        computed: {
            ...mapGetters("production_progress", [
                "recordMaterialsModal",
                "flags",
                "batchMaterialModal"
            ])
        },
        methods: {
            ...mapMutations("app", ["UPDATE_FLAG_SHOW_OVERLAY"]),
            ...mapMutations("production_progress", [
                "UPDATE_RECORD_MATERIALS_MODAL_MATERIALS",
                "UPDATE_RECORD_MATERIALS_MODAL_HAS_CHANGES",
                "UPDATE_RECORD_MATERIALS_MODAL_MATERIALS",
                "UPDATE_RECORD_MATERIALS_MODAL_FILTERED_MATERIALS_LIST",
                "UPDATE_RECORD_MATERIALS_MODAL_HISTORY"
            ]),
            forceRenderField() {
                this.renderField = false;
                this.$nextTick(() => {
                    this.renderField = true;
                });
            },
            formatValue(val) {
                if (val === null || val === undefined) return "0,0000";
                return formatCurrencyWithoutSymbol(val);
            },
            handleFormatterQuantityConsumed(value) {
                const hasMaterial = this.recordMaterialsModal.materials.some((group) =>
                    group.materials.some((m) => m.id === this.material.id)
                );

                if (!hasMaterial || value === "0,0" || !value) return "";

                return formatCurrencyWithoutSymbol(value);
            },
            validateQuantityConsumed() {
                const materialsLocal = [...this.recordMaterialsModal.materials];

                for (let group of materialsLocal) {
                    const materialIndex = group.materials.findIndex(
                        (m) => m.id === this.material.id
                    );

                    if (materialIndex !== -1) {
                        const material = group.materials[materialIndex];
                        const quantity = handleFormatQuantityConsumedToNumber(
                            material.quantityConsumed
                        );
                        const total = (material.alreadyConsumed || 0) + quantity;

                        if (material.required && total === 0) {
                            material.valid = false;
                        } else {
                            material.valid = true;
                        }

                        this.UPDATE_RECORD_MATERIALS_MODAL_MATERIALS(materialsLocal);
                        this.forceRenderField();
                        return;
                    }
                }
            },
            handleOpenRemoveMaterialModal(material) {
                this.$swal({
                    title: this.$t("IntegrationPage.attention"),
                    text:
                        this.$t("ProductionProgress.RemoveNoteAlertPart1") +
                        (material.name || "") +
                        this.$t("ProductionProgress.RemoveNoteAlertPart2"),
                    showCancelButton: true,
                    showCloseButton: true,
                    cancelButtonText: this.$t("MySites.cancel"),
                    confirmButtonText: this.$t("ProductionProgress.Remove"),
                    customClass: {
                        container: "swal-default",
                        confirmButton: "btn btn-primary",
                        cancelButton: "btn btn-outline-danger ml-1"
                    },
                    buttonsStyling: false
                }).then((result) => {
                    if (!result.value) return;

                    this.handleRemoveMaterial(material);
                });
            },
            async handleGetMaterialsHistory() {
                try {
                    const response = await this.getMaterialsHistory();

                    this.UPDATE_RECORD_MATERIALS_MODAL_HISTORY([...response]);
                } finally {
                }
            },
            async getMaterialsHistory() {
                const { order_id } = this.$router.currentRoute.params;
                const GET_MATERIALS_HISTORY_URL = `/api/mysfc/operation/${order_id}/materials-history`;

                try {
                    const response = await this.$http2.get(GET_MATERIALS_HISTORY_URL);

                    const formattedMaterialsHistory = [];

                    response?.data?.data?.forEach((material) => {
                        const { history, ...materialRest } = material;

                        material?.history?.forEach((history) => {
                            formattedMaterialsHistory.push({
                                ...materialRest,
                                ...history,
                                id: materialRest.id,
                                historyId: history.id ?? history.historyId
                            });
                        });
                    });

                    return Promise.resolve(formattedMaterialsHistory);
                } catch (error) {
                    console.error(error);
                    return Promise.reject(error);
                }
            },
            async handleGetMaterials() {
                this.UPDATE_FLAG_SHOW_OVERLAY(true);
                try {
                    const response = await this.getMaterials();

                    const newMaterials = this.handlePersistFilledData(
                        this.recordMaterialsModal.materials,
                        response
                    );

                    let filteredMaterials = [];

                    if (Object.keys(this.batchMaterialModal.batch).length === 0) {
                        filteredMaterials = newMaterials;
                    } else {
                        const filteredByBatch = response.find(
                            (e) => e.batch === this.batchMaterialModal.batch.batch
                        )?.materials;
                        filteredMaterials = filteredByBatch;
                    }
                    this.UPDATE_RECORD_MATERIALS_MODAL_MATERIALS([...response]);
                    filterMaterials(
                        this.recordMaterialsModal.filterInput,
                        filteredMaterials,
                        this.UPDATE_RECORD_MATERIALS_MODAL_FILTERED_MATERIALS_LIST
                    );
                } finally {
                    this.UPDATE_FLAG_SHOW_OVERLAY(false);
                }
            },
            async handleRemoveMaterial(material) {
                this.UPDATE_FLAG_SHOW_OVERLAY(true);
                try {
                    await this.removeMaterial(material);
                    await Promise.all([
                        this.handleGetMaterials(),
                        this.handleGetMaterialsHistory()
                    ]);
                } finally {
                    this.UPDATE_FLAG_SHOW_OVERLAY(false);
                }
            },
            async removeMaterial(material) {
                const { order_id } = this.$router.currentRoute.params;
                const isPointResourcePage =
                    this.$router.currentRoute.name === "operator-point-resource";
                const DELETE_MATERIAL_URL = `/api/mysfc/operation/${isPointResourcePage ? this.orderId : order_id}/material/${material.id}/remove`;

                try {
                    const response = await this.$http2.delete(DELETE_MATERIAL_URL);
                    this.showToast(
                        this.$t("ProductionProgress.Success"),
                        "FrownIcon",
                        this.$t("ProductionProgress.MaterialRemoved"),
                        "success"
                    );
                    return Promise.resolve(response);
                } catch (error) {
                    console.error(error);
                    return Promise.reject(error);
                }
            },
            async getMaterials() {
                const { order_id } = this.$router.currentRoute.params;
                const isPointResourcePage =
                    this.$router.currentRoute.name === "operator-point-resource";
                const GET_MATERIALS_URL = `/api/mysfc/operation/${isPointResourcePage ? this.orderId : order_id}/materials`;

                try {
                    const response = await this.$http2.get(GET_MATERIALS_URL);

                    const formattedMaterials =
                        response?.data?.data?.map((batch) => {
                            const materials = batch.materials.map((material) => {
                                const alreadyConsumed = material.quantityConsumed == null ? 0 : material.quantityConsumed;
                                const remaining = Math.max(0, (material.quantityRequired || 0) - alreadyConsumed);
                                return {
                                    ...material,
                                    alreadyConsumed,
                                    quantityConsumed: formatCurrencyWithoutSymbol(remaining)
                                };
                            });

                            return {
                                ...batch,
                                materials,
                                valid: null,
                                quantityConsumed: formatCurrencyWithoutSymbol(
                                    handleConsumedRequired(
                                        batch.quantityRequired,
                                        batch.quantityConsumed,
                                        batch.required
                                    )
                                )
                            };
                        }) || [];

                    return Promise.resolve(formattedMaterials);
                } catch (error) {
                    console.error(error);
                    return Promise.reject(error);
                }
            },
            handlePersistFilledData(oldList, newList) {
                return (
                    newList?.map((newGroup) => {
                        const oldGroup = oldList.find((g) => {
                            if (newGroup.batch) {
                                return g.batch === newGroup.batch;
                            }
                            return g.batch == null || g.batch === "";
                        });

                        const mergedMaterials = newGroup.materials.map((newMaterial) => {
                            const oldMaterial = oldGroup?.materials.find(
                                (m) => m.id === newMaterial.id
                            );

                            return oldMaterial ? { ...oldMaterial, ...newMaterial } : newMaterial;
                        });

                        return {
                            ...newGroup,
                            materials: mergedMaterials
                        };
                    }) || []
                );
            },
            updateConsumedQuantity(value) {
                const materialsLocal = [...this.recordMaterialsModal.materials];
                const batchToFind = Object.keys(this.batchMaterialModal.batch).length
                    ? this.batchMaterialModal.batch.batch
                    : null;
                const filteredGroup = materialsLocal.find(
                    (group) => group.batch === batchToFind || (!group.batch && !batchToFind)
                );

                if (filteredGroup) {
                    const material = filteredGroup.materials.find((m) => m.id === this.material.id);

                    if (material) {
                        material.quantityConsumed = value;

                        this.UPDATE_RECORD_MATERIALS_MODAL_HAS_CHANGES(true);
                        this.UPDATE_RECORD_MATERIALS_MODAL_MATERIALS(materialsLocal);

                        if (batchToFind) {
                            const filteredByBatch = this.recordMaterialsModal.materials.find(
                                (e) => e.batch === batchToFind
                            );
                            this.UPDATE_RECORD_MATERIALS_MODAL_FILTERED_MATERIALS_LIST(
                                filteredByBatch.materials
                            );
                        }
                    }
                }
            },
            showToast(title, icon, text, variant) {
                this.$toast({
                    component: ToastificationContent,
                    props: {
                        title,
                        icon,
                        text,
                        variant
                    }
                });
            }
        }
    };
</script>

<style lang="scss">
    .record-materials-modal-material__quantity-box {
        position: relative;

        .form-group {
            margin-bottom: 0;
        }

        .invalid-feedback {
            text-align: start !important;
        }

        .invalid-feedback {
            position: absolute;
            top: 100%;
        }
    }
    .record-materials-modal-material__quantity-input {
        text-align: center;
    }

    @media (max-width: 768px) and (min-width: 571px) {
        .record-materials-modal-material__quantity-input {
            height: 50px !important;
        }
    }
</style>

<style lang="scss" scoped>
    .record-materials-modal-material__container {
        padding: 31.5px 29px;
        display: flex;
        flex-wrap: nowrap;
        gap: 16px;
        align-items: center;
        border-bottom: 1px solid #cfc4be;

        .record-materials-modal-material__content {
            display: flex;
            flex-direction: column;
            gap: 16px;
            width: 100%;

            .record-materials-modal-material__name-header {
                color: #4c4541;
                font-size: 18px;
                font-weight: 700;
                line-height: 26px;
                margin-bottom: 4px;
                white-space: normal;
                word-break: break-word;
            }

            .record-materials-modal-material__fields-grid {
                display: flex;
                flex-direction: column;
                gap: 12px;

                .record-materials-modal-material__field-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    width: 100%;

                    &.--input-row {
                        margin-top: 4px;
                    }

                    .record-materials-modal-material__field-label {
                        color: #7e7570;
                        font-size: 14px;
                        font-weight: 500;
                        line-height: 20px;
                        min-width: 180px;

                        &.--highlight {
                            color: #4c4541;
                            font-weight: 600;
                        }
                    }

                    .record-materials-modal-material__field-value {
                        color: #4c4541;
                        font-size: 14px;
                        font-weight: 600;
                        line-height: 20px;
                        text-align: right;
                    }

                    .record-materials-modal-material__quantity-box {
                        width: 180px;
                        color: #4c4541;
                        text-align: center;
                        font-size: 14px;
                        font-weight: 400;
                        line-height: 20px;
                    }
                }
            }
        }

        .record-materials-modal-material__close-button-container {
            padding: 12px;
            cursor: pointer;

            svg {
                min-height: 14px;
                max-height: 14px;
                min-width: 14px;
                max-width: 14px;
                fill: #d32f2f;
            }
        }
    }

    @media (max-width: 768px) and (min-width: 571px) {
        .record-materials-modal-material__container {
            .record-materials-modal-material__content {
                .record-materials-modal-material__name-header {
                    font-size: 24px !important;
                    line-height: 32px !important;
                }

                .record-materials-modal-material__fields-grid {
                    .record-materials-modal-material__field-row {
                        .record-materials-modal-material__field-label {
                            font-size: 20px !important;
                            line-height: 28px !important;
                            min-width: 250px !important;
                        }

                        .record-materials-modal-material__field-value {
                            font-size: 20px !important;
                            line-height: 28px !important;
                        }

                        .record-materials-modal-material__quantity-box {
                            width: 250px !important;
                        }
                    }
                }
            }

            .record-materials-modal-material__close-button-container {
                padding: 14px !important;

                svg {
                    min-width: 18px !important;
                    max-width: 18px !important;
                    min-height: 18px !important;
                    max-height: 18px !important;
                }
            }
        }
    }

    @media (max-width: 570px) {
        .record-materials-modal-material__container {
            padding: 23.5px 16px;

            &:last-child {
                padding: 24px 16px;
            }

            .record-materials-modal-material__content {
                .record-materials-modal-material__name-header {
                    font-size: 16px !important;
                    line-height: 22px !important;
                }

                .record-materials-modal-material__fields-grid {
                    .record-materials-modal-material__field-row {
                        .record-materials-modal-material__field-label {
                            font-size: 12px !important;
                            line-height: 16px !important;
                            min-width: 120px !important;
                        }

                        .record-materials-modal-material__field-value {
                            font-size: 13px !important;
                            line-height: 18px !important;
                        }

                        .record-materials-modal-material__quantity-box {
                            width: 120px !important;
                        }
                    }
                }
            }
        }
    }
</style>
